// 3rd party
import chroma from 'chroma-js';
import moment from 'moment';
import deepmerge from 'deepmerge';
import { merge } from 'lodash';

// components
import { LineChartDatum, LineChartOptions } from '@/views/components/charts/LineChart';

// utils
import { formatMessage, formatLocalizedString, formatNumber } from '@/utils/i18n';
import { calculateDrawdownData, DrawdownData, trimRollingData } from '@/utils/calculation-utils';
import { isBenchmark, getCommonDataPoints } from '@/utils/comparison';
import { calculateGrowth } from '@/utils/portfolio/utils';
import {
  mergeChartItems,
  getExpandedPortfolioItems,
  getDashStyle,
  getLineWidth,
  trimComparisonItemsData,
  getRollingIntervalFromDates,
  MAX_START_DATE,
  ChartItem,
} from '@/utils/charts';
import { currencyToText } from '@/utils/currency';
import { deepClone } from '@/utils/objects';
import { getComparisonItemHoldings } from '@/utils/comparison/items';
import { capitalizeFirst } from '@/utils/strings';
import portfolioUtils from '@/utils/portfolio';

// types
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { commonPlotTooltipFormatter, getSharedTooltip } from '@/views/components/charts/utils/tooltip';
import { getThinDashedLineCrosshair } from '@/views/components/charts/utils/crosshair';
import LocalizedString from '@/types/localized-string';

// Chart options
export function getChartOptions(height?: number): LineChartOptions {
  return {
    chart: { height },
    xAxis: {
      crosshair: {
        ...getThinDashedLineCrosshair(),
        snap: false,
      },
    },
    yAxis: {
      title: { text: formatMessage('Charts.Drawdown.AxisLabels.Y') },
      labels: { y: 6, x: -6 },
      max: 0,
      showLastLabel: true,
    },
    tooltip: getSharedTooltip(commonPlotTooltipFormatter),
  };
}

function getBaseOptions(
  item: {
    _id: string;
    isFX: boolean | undefined;
    currency: string | undefined;
    type: string;
    isSubHolding: boolean;
    drawdownData: DrawdownData;
    rollingBackfillTimestamp?: number;
    color: string | undefined;
    name: LocalizedString;
  },
  localCurrency: string,
  fillArea?: boolean,
  showBaseCurrencies?: boolean,
) {
  let baseOptions: any = {
    color: item.color,
    dashStyle: getDashStyle(item, showBaseCurrencies),
    lineWidth: getLineWidth(item, showBaseCurrencies),
    type: fillArea ? 'area' : 'line',
    tooltip: {
      valuePrefix: showBaseCurrencies ? formatMessage('Charts.CADConverted') : undefined,
      valueSuffix: `% ${currencyToText(localCurrency)}`,
      ...getSharedTooltip(commonPlotTooltipFormatter),
    },
    fillColor: !fillArea
      ? undefined
      : {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [
              0,
              item.color
                ? chroma(item.color)
                    .alpha(item.isSubHolding ? 0.01 : 0.03)
                    .hex()
                : '',
            ],
            [
              1,
              item.color
                ? chroma(item.color)
                    .alpha(item.isSubHolding ? 0.1 : 0.3)
                    .hex()
                : '',
            ],
          ],
        },
  };

  if (isBenchmark(item)) {
    baseOptions = {
      ...baseOptions,
      fillColor: !fillArea
        ? undefined
        : {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0, item.color ? chroma(item.color).alpha(0.0).hex() : ''],
              [1, item.color ? chroma(item.color).alpha(0.2).hex() : ''],
            ],
          },
    };
  }

  return baseOptions;
}

function createShortItemLineData(
  drawdownData: DrawdownData,
  item: { name: LocalizedString; isFX: boolean | undefined; isSubHolding: boolean; type: string },
  baseOptions: any,
  breakingPoint: number,
  minDate: number,
  maxDate: number,
) {
  const beforeData = trimRollingData(drawdownData.drawdowns, minDate, breakingPoint);
  const afterData = trimRollingData(drawdownData.drawdowns, breakingPoint, maxDate);
  const itemName = formatLocalizedString(item.name);
  return [
    {
      ...baseOptions,
      itemType: item.type,
      data: beforeData,
      name: `${itemName} - ${formatMessage('Charts.Incomplete')}` || '',
      dashStyle: getDashStyle(item, item.isFX, true),
      lineWidth: getLineWidth(item, item.isFX),
    },
    {
      ...baseOptions,
      itemType: item.type,
      data: afterData,
      name: itemName || '',
      dashStyle: getDashStyle(item, item.isFX, false),
      lineWidth: getLineWidth(item, item.isFX),
    },
  ];
}

function createBaseCurrencyLineData(
  drawdownData: DrawdownData,
  item: { name: LocalizedString; currency: string | undefined; isFX: boolean | undefined; isSubHolding: boolean; type: string },
  baseOptions: any,
) {
  return {
    ...baseOptions,
    data: drawdownData.drawdowns,
    name: formatLocalizedString(item.name),
    dashStyle: getDashStyle(item),
    lineWidth: getLineWidth(item),
    tooltip: {
      valuePrefix: `(${currencyToText(item.currency)})`,
      valueSuffix: `% ${currencyToText(item.currency)}`,
      ...getSharedTooltip(commonPlotTooltipFormatter),
    },
  };
}

export function formatItemsForHistoricalScenarios({
  data,
  fillArea,
  showBaseCurrencies,
  localCurrency,
}: {
  data: {
    _id: string;
    isFX: boolean | undefined;
    currency: string | undefined;
    type: string;
    isSubHolding: boolean;
    drawdownData: DrawdownData;
    rollingBackfillTimestamp?: number;
    color: string | undefined;
    name: LocalizedString;
  }[];
  fillArea?: boolean;
  showBaseCurrencies?: boolean;
  localCurrency: any;
}) {
  return data.reduce((acc: any[], item) => {
    const shortItem = !!item.rollingBackfillTimestamp;
    const baseOptions: any = getBaseOptions(item, localCurrency, fillArea, showBaseCurrencies);

    // Show items in CAD
    if (item.isFX || !showBaseCurrencies) {
      if (item.drawdownData.drawdowns.length < 2) return acc;
      if (shortItem) {
        const breakingPoint = item.rollingBackfillTimestamp!;
        const data = createShortItemLineData(
          item.drawdownData,
          item,
          baseOptions,
          breakingPoint,
          item.drawdownData.drawdowns[0][0],
          item.drawdownData.drawdowns[item.drawdownData.drawdowns.length - 1][0],
        );
        acc.push(...data);
      } else {
        acc.push({
          ...baseOptions,
          name: formatLocalizedString(item.name),
          data: item.drawdownData.drawdowns,
        });
      }
    }

    // Show items in base currency
    if (showBaseCurrencies) {
      acc.push(createBaseCurrencyLineData(item.drawdownData, item, baseOptions));
    }

    return acc;
  }, []);
}

// Format items for drawdown chart
export function formatItems({
  items,
  expandedPortfoliosData,
  fillArea,
  showBaseCurrencies,
  shortHistory,
  localCurrency,
  appRegion,
  startDate,
  endDate,
  useMonthly,
}: {
  items: any[];
  expandedPortfoliosData?: any;
  fillArea?: boolean;
  showBaseCurrencies?: boolean;
  shortHistory?: boolean;
  localCurrency: any;
  appRegion: any;
  startDate: Date | null;
  endDate: Date;
  useMonthly: boolean;
}) {
  const interval = getRollingIntervalFromDates(startDate, endDate, items, useMonthly, shortHistory);
  const portfolioHoldingItems = getExpandedPortfolioItems(items, appRegion, expandedPortfoliosData);
  const chartItems = mergeChartItems(items, portfolioHoldingItems);
  const date = startDate || MAX_START_DATE;
  const trimmedItems = trimComparisonItemsData(chartItems, date, endDate, interval, shortHistory) as ChartItem[];

  return trimmedItems.reduce((acc: any[], item: any) => {
    const shortItem = shortHistory && item.isShort && (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.BENCHMARK);

    const baseOptions: any = getBaseOptions(item, localCurrency, fillArea, showBaseCurrencies);

    // Show items in CAD
    if (item.isFX || !showBaseCurrencies) {
      const fees = (item.discretionaryMer || 0) + (item.tax || 0);
      const rollingDataCAD = calculateGrowth(getComparisonItemHoldings(item, true), fees, undefined, shortHistory);
      if (!rollingDataCAD || !rollingDataCAD[interval]) return acc;
      item.performance.rolling = rollingDataCAD;
      const drawdownData = calculateDrawdownData(rollingDataCAD[interval]);
      if (drawdownData.drawdowns.length < 2) return acc;

      if (shortItem) {
        const breakingPoint = getCommonDataPoints([item], interval, date, 'ASC')[interval];
        const data = createShortItemLineData(item.drawdownData, item, baseOptions, breakingPoint, date.getTime(), Date.now());
        acc.push(...data);
      } else {
        acc.push({
          ...baseOptions,
          name: formatLocalizedString(item.name),
          data: drawdownData.drawdowns,
        });
      }
    }

    // Show items in base currency
    if (showBaseCurrencies) {
      const fees = (item.discretionaryMer || 0) + (item.tax || 0);

      let rollingDataBase;
      if (item.performanceBase) {
        const holdings = deepClone(getComparisonItemHoldings(item, true)).map((h: any) => ({ ...h, rolling: h.baseRolling }));
        rollingDataBase = portfolioUtils.calculateGrowth(holdings, fees, undefined, shortHistory);
      }

      if (!rollingDataBase || !rollingDataBase[interval]) return acc;

      const drawdownData = calculateDrawdownData(rollingDataBase[interval]);
      if (drawdownData.drawdowns.length < 2) return acc;

      acc.push(createBaseCurrencyLineData(drawdownData, item, baseOptions));
    }

    return acc;
  }, []);
}

export function getDefaultChartOptions({
  height,
  yAxisOpposite,
  animate,
  optionsOverrides = {},
}: {
  height?: number | null;
  yAxisOpposite?: boolean;
  animate?: boolean;
  optionsOverrides?: Record<string, any>;
} = {}): LineChartOptions {
  const marker: Highcharts.PointMarkerOptionsObject = {
    symbol: 'circle',
    radius: 0,
    states: {
      hover: {
        lineWidth: 3,
        radius: 5,
      },
      inactive: {
        opacity: 0,
      },
    },
  };
  const baseOptions = {
    series: [] as Highcharts.SeriesLineOptions[],
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    navigator: { enabled: false },
    chart: {
      height: height ?? undefined,
      spacingLeft: 0,
      spacingRight: 0,
      panning: { enabled: false },
    },
    tooltip: getSharedTooltip(commonPlotTooltipFormatter),
    title: {
      text: undefined,
    },
    xAxis: {
      crosshair: getThinDashedLineCrosshair(),
      minTickInterval: 2735696401, // 1 month in milliseconds
      tickWidth: 0,
      lineWidth: 0,
      labels: {
        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
          return capitalizeFirst(moment(new Date(this.value)).format('MMM YYYY'));
        },
      },
    },
    yAxis: {
      title: {
        text: null,
      },
      opposite: yAxisOpposite ?? false,
      labels: {
        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
          return formatNumber(Number(this.value), 1);
        },
      },
    },
    plotOptions: {
      area: { marker },
      line: { marker },
      series: {
        animation: animate ?? true,
      },
    },
  };

  return merge({}, baseOptions, optionsOverrides);
}

export function getMergedHighchartsOptions(defaultOptions: LineChartOptions, customOptions: LineChartOptions, data: LineChartDatum[]): LineChartOptions {
  return {
    ...deepmerge(defaultOptions, customOptions || {}),
    series: data,
  };
}
